// src/theme/tokens.js
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for all AHEDGE brand design tokens.
//
// These values are mirrored as CSS custom properties in src/index.css
// and as Tailwind color utilities in tailwind.config.js.
//
// Naming convention:
//   CSS variable:     --ah-{group}-{concept}-{variant}
//   Tailwind utility: ah-{group}-{concept}-{variant}
// ─────────────────────────────────────────────────────────────────────────────

export const brandTokens = {
  // ── Brand Primary ──────────────────────────────────────────────────────────
  brand: {
    primary: '#4472C4', // --ah-brand-primary       (buttons, links, focus rings)
    primaryHover: '#3761B5', // --ah-brand-primary-hover (hover / active state)
    primaryAlt: '#4B70BB', // --ah-brand-primary-alt   (secondary button variant)
    primaryForm: '#4472CF', // --ah-brand-primary-form  (form focus, checkboxes)
    accent: '#2C56C9', // --ah-brand-accent        (section headings)
    icon: '#155DFC', // --ah-brand-icon          (icon accent, bright)
    outline: '#1259AB', // --ah-brand-outline       (outline button text)
    altHover: '#3a5fa3', // --ah-brand-alt-hover     (skills/cert alt hover)
  },

  // ── Sidebar / Navigation ───────────────────────────────────────────────────
  sidebar: {
    gradientStart: '#34589E', // --ah-sidebar-gradient-start
    gradientMid: '#2A4A8C', // --ah-sidebar-gradient-mid
    gradientEnd: '#1E345F', // --ah-sidebar-gradient-end
    dropdownBg: '#355283', // --ah-sidebar-dropdown-bg
    activeIndicator: '#00B8D3', // --ah-sidebar-active-indicator
    avatarEnd: '#2A5CAA', // --ah-sidebar-avatar-end
  },

  // ── Header ─────────────────────────────────────────────────────────────────
  header: {
    from: '#005BBB', // --ah-header-from
    to: '#005ABA', // --ah-header-to
  },

  // ── Surfaces / Backgrounds ─────────────────────────────────────────────────
  surface: {
    page: '#F4F7FC', // --ah-surface-page         (main content area bg)
    panel: '#F1F5F9', // --ah-surface-panel        (top bar / sticky header)
    cardBorder: '#E2EAFB', // --ah-surface-card-border  (card borders + subtle bg)
    iconBg: '#EFF6FF', // --ah-surface-icon-bg      (icon containers = blue-50)
    formSection: '#F0F6FF', // --ah-surface-form-section (enabled input bg)
    hover: '#F8FAFF', // --ah-surface-hover        (row / item hover bg)
    input: '#f5f8fb', // --ah-surface-input        (shared input/control bg)
    progress: '#eef0f5', // --ah-surface-progress     (progress bar track)
    processIcon: '#eef2ff', // --ah-surface-process-icon (processing overlay icon)
  },

  // ── Nav Icon Accents ────────────────────────────────────────────────────────
  navIcon: {
    blue: '#33d6f0', // --ah-nav-icon-blue
    dark: '#00d4f0', // --ah-nav-icon-dark
    teal: '#80deea', // --ah-nav-icon-teal
    orange: '#ffb74d', // --ah-nav-icon-orange
    pink: '#f48fb1', // --ah-nav-icon-pink
  },

  // ── Text ────────────────────────────────────────────────────────────────────
  text: {
    heading: '#1e2d40', // --ah-text-heading        (dark heading text)
    body: '#5A6785', // --ah-text-body           (body / label text)
    secondary: '#7b8fa6', // --ah-text-secondary      (secondary body)
    placeholder: '#8C96AB', // --ah-text-placeholder    (placeholder / tertiary)
    darkest: '#1a1f36', // --ah-text-darkest        (progress headings)
    muted: '#8a91a8', // --ah-text-muted          (muted text)
    disabled: '#aab0c2', // --ah-text-disabled       (disabled / faint)
    processActive: '#1a3a8f', // --ah-text-process-active (active processing step)
  },

  // ── Status / Feedback ──────────────────────────────────────────────────────
  status: {
    success: '#059669', // --ah-status-success         (emerald success)
    successDark: '#1e9e5c', // --ah-status-success-dark
    successLight: '#3ecf8e', // --ah-status-success-light   (progress gradient end)
    successCheckbox: '#2E8B6A', // --ah-status-success-checkbox
    successBg: '#e8f7ef', // --ah-status-success-bg      (success icon bg)
    error: '#dc2626', // --ah-status-error
    errorHoverBg: '#FDECEC', // --ah-status-error-hover-bg  (delete hover)
    toastBg: '#0f172a', // --ah-toast-bg               (toast = slate-950)
  },

  // ── Borders / Decorative ───────────────────────────────────────────────────
  border: {
    progress: '#d8dbe8', // --ah-border-progress
    hover: '#B8CAE6', // --ah-border-hover
    card: '#D4DEEF', // --ah-border-card        (scrollbar track)
    cardLight: '#C4D0E8', // --ah-border-card-light  (scrollbar thumb)
    progressActive: '#1a3a8f', // --ah-border-progress-active
    indicatorBg: '#eef2ff', // --ah-progress-indicator-bg
  },
};

// ── Flat map for CSS variable injection (used by ThemeProvider) ───────────────
// Key = CSS variable name without leading `--`, value = hex
export const cssVarMap = {
  'ah-brand-primary': brandTokens.brand.primary,
  'ah-brand-primary-hover': brandTokens.brand.primaryHover,
  'ah-brand-primary-alt': brandTokens.brand.primaryAlt,
  'ah-brand-primary-form': brandTokens.brand.primaryForm,
  'ah-brand-accent': brandTokens.brand.accent,
  'ah-brand-icon': brandTokens.brand.icon,
  'ah-brand-outline': brandTokens.brand.outline,
  'ah-brand-alt-hover': brandTokens.brand.altHover,

  'ah-sidebar-gradient-start': brandTokens.sidebar.gradientStart,
  'ah-sidebar-gradient-mid': brandTokens.sidebar.gradientMid,
  'ah-sidebar-gradient-end': brandTokens.sidebar.gradientEnd,
  'ah-sidebar-dropdown-bg': brandTokens.sidebar.dropdownBg,
  'ah-sidebar-active-indicator': brandTokens.sidebar.activeIndicator,
  'ah-sidebar-avatar-end': brandTokens.sidebar.avatarEnd,

  'ah-header-from': brandTokens.header.from,
  'ah-header-to': brandTokens.header.to,

  'ah-surface-page': brandTokens.surface.page,
  'ah-surface-panel': brandTokens.surface.panel,
  'ah-surface-card-border': brandTokens.surface.cardBorder,
  'ah-surface-icon-bg': brandTokens.surface.iconBg,
  'ah-surface-form-section': brandTokens.surface.formSection,
  'ah-surface-hover': brandTokens.surface.hover,
  'ah-surface-input': brandTokens.surface.input,
  'ah-surface-progress': brandTokens.surface.progress,
  'ah-surface-process-icon': brandTokens.surface.processIcon,

  'ah-nav-icon-blue': brandTokens.navIcon.blue,
  'ah-nav-icon-dark': brandTokens.navIcon.dark,
  'ah-nav-icon-teal': brandTokens.navIcon.teal,
  'ah-nav-icon-orange': brandTokens.navIcon.orange,
  'ah-nav-icon-pink': brandTokens.navIcon.pink,

  'ah-text-heading': brandTokens.text.heading,
  'ah-text-body': brandTokens.text.body,
  'ah-text-secondary': brandTokens.text.secondary,
  'ah-text-placeholder': brandTokens.text.placeholder,
  'ah-text-darkest': brandTokens.text.darkest,
  'ah-text-muted': brandTokens.text.muted,
  'ah-text-disabled': brandTokens.text.disabled,
  'ah-text-process-active': brandTokens.text.processActive,

  'ah-status-success': brandTokens.status.success,
  'ah-status-success-dark': brandTokens.status.successDark,
  'ah-status-success-light': brandTokens.status.successLight,
  'ah-status-success-checkbox': brandTokens.status.successCheckbox,
  'ah-status-success-bg': brandTokens.status.successBg,
  'ah-status-error': brandTokens.status.error,
  'ah-status-error-hover-bg': brandTokens.status.errorHoverBg,
  'ah-toast-bg': brandTokens.status.toastBg,

  'ah-border-progress': brandTokens.border.progress,
  'ah-border-hover': brandTokens.border.hover,
  'ah-border-card': brandTokens.border.card,
  'ah-border-card-light': brandTokens.border.cardLight,
  'ah-border-progress-active': brandTokens.border.progressActive,
  'ah-progress-indicator-bg': brandTokens.border.indicatorBg,
};

// ── Dark theme tokens (design doc §3.2) ──────────────────────────────────────
export const darkTokens = {
  brand: {
    primary: '#4F7BCB',
    primaryHover: '#3D68B8',
    primaryAlt: '#5580C8',
    primaryForm: '#4F7BCB',
    accent: '#3D6AC0',
    icon: '#6B9CE8',
    outline: '#4F7BCB',
    altHover: '#3D68B8',
  },
  sidebar: {
    gradientStart: '#1B2C4E',
    gradientMid: '#16243F',
    gradientEnd: '#101B30',
    dropdownBg: '#162240',
    activeIndicator: '#22D4EF',
    avatarEnd: '#1C3A6E',
  },
  header: {
    from: '#0A1B3A',
    to: '#0A1B3A',
  },
  surface: {
    page: '#0B1220',
    panel: '#131C2E',
    cardBorder: '#1C2E4A',
    iconBg: '#1C2942',
    formSection: '#131C2E',
    hover: '#1B2538',
    input: '#131C2E',
    progress: '#1B2538',
    processIcon: '#1C2942',
  },
  navIcon: {
    blue: '#33d6f0',
    dark: '#00d4f0',
    teal: '#80deea',
    orange: '#ffb74d',
    pink: '#f48fb1',
  },
  text: {
    heading: '#EAEFF8',
    body: '#AAB8D0',
    secondary: '#8493AC',
    placeholder: '#5E6E88',
    darkest: '#F0F4FC',
    muted: '#8493AC',
    disabled: '#4A5A72',
    processActive: '#6B9CE8',
  },
  status: {
    success: '#34D399',
    successDark: '#10B981',
    successLight: '#6EE7B7',
    successCheckbox: '#34D399',
    successBg: 'rgba(52,211,153,0.15)',
    error: '#F87171',
    errorHoverBg: 'rgba(248,113,113,0.15)',
    toastBg: '#0B1220',
  },
  border: {
    progress: '#2E3D58',
    hover: '#3A4A66',
    card: '#3A4A66',
    cardLight: '#2E3D58',
    progressActive: '#4F7BCB',
    indicatorBg: '#1C2942',
  },
};

// Flat map for runtime CSS variable injection in dark mode
export const darkCssVarMap = {
  'ah-brand-primary': darkTokens.brand.primary,
  'ah-brand-primary-hover': darkTokens.brand.primaryHover,
  'ah-brand-primary-alt': darkTokens.brand.primaryAlt,
  'ah-brand-primary-form': darkTokens.brand.primaryForm,
  'ah-brand-accent': darkTokens.brand.accent,
  'ah-brand-icon': darkTokens.brand.icon,
  'ah-brand-outline': darkTokens.brand.outline,
  'ah-brand-alt-hover': darkTokens.brand.altHover,

  'ah-sidebar-gradient-start': darkTokens.sidebar.gradientStart,
  'ah-sidebar-gradient-mid': darkTokens.sidebar.gradientMid,
  'ah-sidebar-gradient-end': darkTokens.sidebar.gradientEnd,
  'ah-sidebar-dropdown-bg': darkTokens.sidebar.dropdownBg,
  'ah-sidebar-active-indicator': darkTokens.sidebar.activeIndicator,
  'ah-sidebar-avatar-end': darkTokens.sidebar.avatarEnd,

  'ah-header-from': darkTokens.header.from,
  'ah-header-to': darkTokens.header.to,

  'ah-surface-page': darkTokens.surface.page,
  'ah-surface-panel': darkTokens.surface.panel,
  'ah-surface-card-border': darkTokens.surface.cardBorder,
  'ah-surface-icon-bg': darkTokens.surface.iconBg,
  'ah-surface-form-section': darkTokens.surface.formSection,
  'ah-surface-hover': darkTokens.surface.hover,
  'ah-surface-input': darkTokens.surface.input,
  'ah-surface-progress': darkTokens.surface.progress,
  'ah-surface-process-icon': darkTokens.surface.processIcon,

  'ah-nav-icon-blue': darkTokens.navIcon.blue,
  'ah-nav-icon-dark': darkTokens.navIcon.dark,
  'ah-nav-icon-teal': darkTokens.navIcon.teal,
  'ah-nav-icon-orange': darkTokens.navIcon.orange,
  'ah-nav-icon-pink': darkTokens.navIcon.pink,

  'ah-text-heading': darkTokens.text.heading,
  'ah-text-body': darkTokens.text.body,
  'ah-text-secondary': darkTokens.text.secondary,
  'ah-text-placeholder': darkTokens.text.placeholder,
  'ah-text-darkest': darkTokens.text.darkest,
  'ah-text-muted': darkTokens.text.muted,
  'ah-text-disabled': darkTokens.text.disabled,
  'ah-text-process-active': darkTokens.text.processActive,

  'ah-status-success': darkTokens.status.success,
  'ah-status-success-dark': darkTokens.status.successDark,
  'ah-status-success-light': darkTokens.status.successLight,
  'ah-status-success-checkbox': darkTokens.status.successCheckbox,
  'ah-status-success-bg': darkTokens.status.successBg,
  'ah-status-error': darkTokens.status.error,
  'ah-status-error-hover-bg': darkTokens.status.errorHoverBg,
  'ah-toast-bg': darkTokens.status.toastBg,

  'ah-border-progress': darkTokens.border.progress,
  'ah-border-hover': darkTokens.border.hover,
  'ah-border-card': darkTokens.border.card,
  'ah-border-card-light': darkTokens.border.cardLight,
  'ah-border-progress-active': darkTokens.border.progressActive,
  'ah-progress-indicator-bg': darkTokens.border.indicatorBg,
};

export default brandTokens;
